import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { SUBMISSIONS } from "../graphql/queries";

export const useSubmissions = (user, userRole) => {
  const [submissions, setSubmissions] = useState({
    submittersList: [],
    individualSubmissions: [],
    selectedSubmission: {},
    summarySubmissions: [],
    selectedSummarySubmission: {},
    summarySubmittersList: [],
  });
  const { id } = useParams();
  const [getSubmissions] = useLazyQuery(SUBMISSIONS, {
    onCompleted: async (data) => {
      const submissionsData =
        userRole !== "Student"
          ? data.submissions
              .filter((sub) => sub.quizAnswers.length > 0)
              .reduce((result, submission) => {
                const { submitter, submittedAt } = submission;
                const index = result.findIndex(
                  (a) => a.submitter.id === submitter.id
                );
                if (index > -1) {
                  const currentSubmittedAt = new Date(submittedAt).getTime();
                  const existingSubmittedAt = new Date(
                    result[index].submittedAt
                  ).getTime();
                  if (currentSubmittedAt > existingSubmittedAt)
                    result[index] = submission;
                } else {
                  result.push(submission);
                }
                return result;
              }, [])
          : data.submissions.filter((sub) => sub.quizAnswers.length > 0);
      let individualSubmissions = submissionsData.map((submission) => {
        let quizFormSubmittedAt =
          submission.submittedAt && new Date(submission.submittedAt);
        let quizFormReviewedAt =
          submission.reviewedAt && new Date(submission.reviewedAt);
        let submittedAt =
          submission.submittedAt && new Date(submission.submittedAt);
        let reviewedAt =
          submission.reviewedAt && new Date(submission.reviewedAt);
        const getMaxTime =
          submittedAt && reviewedAt
            ? submittedAt.getTime() > reviewedAt.getTime()
              ? quizFormSubmittedAt
              : quizFormReviewedAt
            : quizFormSubmittedAt;
        let toReview = false;
        if (!quizFormReviewedAt) {
          toReview = true;
        } else if (quizFormSubmittedAt === quizFormReviewedAt) {
          toReview = false;
        } else {
          toReview = getMaxTime === quizFormSubmittedAt;
        }
        return {
          ...submission,
          toReview: toReview,
        };
      });
      let submittersList = submissionsData?.map((ir) => ({
        id: ir?.id,
        name: `${ir?.submitter?.firstName} ${ir?.submitter?.lastName}`,
        toReview: ir?.toReview,
        userPicture: ir?.avatarColor,
      }));
      let summarySubmittersList = submissionsData?.map((submission) => ({
        id: submission?.submitter?.id,
        name: `${submission?.submitter?.firstName} ${submission?.submitter?.lastName}`,
      }));
      let selectedSubmission = {};
      let summarySubmissions = [];
      let selectedSummarySubmission = {};
      if (submissionsData?.length > 0) {
        summarySubmissions = submissionsData[0]?.quizAnswers?.map((qa) => ({
          id: qa?.id,
          name: qa?.variable?.variableAttributes?.title,
          tableData: summarySubmittersList?.map((student) => {
            const index = submissionsData?.findIndex(
              (submission) => submission?.submitter?.id === student?.id
            );
            if (index > -1) {
              const submission = submissionsData[index];
              const quizAnswer = submission?.quizAnswers.find(
                (q) => q.variable.id === qa.variable.id
              );
              const point = quizAnswer?.variable?.possibleAnswers?.find(
                (pa) => pa.value === quizAnswer.value
              );
              return {
                id: qa.id,
                responder: student.name,
                response: quizAnswer?.value,
                points: point ? point?.maxPoints : 0,
              };
            }
            return false;
          }),
        }));
        selectedSummarySubmission = summarySubmissions[0];
      }
      const submissionItem =
        submissionsData.length > 0 ? submissionsData[0] : null;
      if (submissionItem) {
        selectedSubmission = {
          id: submissionItem?.id,
          name: `${submissionItem?.submitter?.firstName} ${submissionItem?.submitter?.lastName}`,
          submittedAt: new Date(submissionItem?.submittedAt),
          tableData: submissionItem?.quizAnswers?.map((qa) => {
            const point = qa?.variable?.possibleAnswers?.find(
              (pa) => pa.value === qa.value
            );
            return {
              id: qa?.id,
              title: qa?.variable?.variableAttributes?.title,
              response: !qa?.value ? "" : qa?.value,
              correctAnswer: qa?.variable?.possibleAnswers[0]?.value,
              points: `${point ? point?.maxPoints : 0}/${
                qa?.variable?.possibleAnswers[0]?.maxPoints
              }`,
              comments: !qa?.variable?.teacherComment
                ? ""
                : qa?.variable?.teacherComment,
            };
          }),
        };
      }
      setSubmissions({
        submittersList,
        individualSubmissions,
        selectedSubmission,
        summarySubmissions,
        selectedSummarySubmission,
        summarySubmittersList,
      });
    },
    onError: () => {
      // do nothing
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!!id) {
      getSubmissions({
        variables: {
          where: {
            AND: [
              { dyno: { id: { equals: id } } },
              //  ,{ organizationClass: { id: {equals: "${classId}"}}}
              { submittedAt: { not: { equals: null } } },
              { isLocked: { not: { equals: false } } },
              ...(userRole === "Student"
                ? [
                    {
                      submitter: {
                        id: { equals: user.id },
                      },
                    },
                  ]
                : [
                    {
                      submitter: {
                        id: { not: { equals: user.id } },
                      },
                    },
                  ]),
            ],
          },
        },
      });
    }
  }, [id, getSubmissions, user.id, userRole]);

  const selectSubmission = (id) => {
    const submissionItem = submissions?.individualSubmissions?.find(
      (ir) => ir?.id === id
    );
    setSubmissions({
      ...submissions,
      selectedSubmission: {
        id: submissionItem?.id,
        name: `${submissionItem?.submitter?.firstName} ${submissionItem?.submitter?.lastName}`,
        submittedAt: new Date(submissionItem.submittedAt),
        tableData: submissionItem?.quizAnswers?.map((qa) => {
          const point = qa?.variable?.possibleAnswers?.find(
            (pa) => pa.value === qa.value
          );
          return {
            id: qa?.id,
            title: qa?.variable?.variableAttributes?.title,
            response: !qa?.value ? "" : qa?.value,
            correctAnswer: qa?.variable?.possibleAnswers[0]?.value,
            points: `${point ? point?.maxPoints : 0}/${
              qa?.variable?.possibleAnswers[0]?.maxPoints
            }`,
            comments: !qa?.variable?.teacherComment
              ? ""
              : qa?.variable?.teacherComment,
          };
        }),
      },
    });
  };

  const selectSummarySubmission = (id) => {
    const selectedSummarySubmission = submissions?.summarySubmissions?.find(
      (ss) => ss?.id === id
    );
    setSubmissions({
      ...submissions,
      selectedSummarySubmission,
    });
  };

  return { submissions, selectSubmission, selectSummarySubmission };
};
